// React
import React from 'react';
// External
import { withRouter, Link } from 'react-router-dom';
// Internal
import NavbarPrimary from 'components/shared/organisms/NavbarPrimary/NavbarPrimary';
import NavbarSecondary from 'components/shared/organisms/NavbarSecondary/NavbarSecondary';
import PageTitle from 'components/shared/atoms/PageTitle/PageTitle';
import ContentCard from 'components/shared/organisms/ContentCard/ContentCard';
import CustomTable from 'components/shared/molecules/CustomTable/CustomTable';
import Pagination from 'components/shared/molecules/Pagination/Pagination';
import { getBookings, getOrderStages, getVetServices } from 'services/booking';
import newBooking from 'models/Booking';
import ContainerHeader from 'components/shared/molecules/ContainerHeader/ContainerHeader';
import Filter from './Filter/Filter';

// Styles
import './styles.scss';
import EDIT_BOOKING_PATH from 'constants/editBookingPath';
import BookingImage from 'components/shared/atoms/BookingImage/BookingImage';
import imageFallback from 'assets/images/image-not-available.png';

class Bookings extends React.Component {
    debounceTimeout = 0;

    constructor(props) {
        super(props);
        const globalParams = new URLSearchParams(window.location.search);
        const page = globalParams.get('page') || 1;
        this.state = {
            allBookings: [],
            bookingList: [],
            currentPage: page,
            lastPage: null,
            objLightBox: {},
            statusDisplayMap: {},
            serviceTypeList: [],
            currentFilter: {
                search_term: globalParams.get('search_term') || '',
                service_type: globalParams.get('service_type')?.replace('*', '&') || '',
                service_status: globalParams.get('service_status') ? globalParams.get('service_status').split(',') : [],
                service_status_label: globalParams.get('service_status_label') || '',
                date_created_from: globalParams.get('date_created_from') || '',
                date_created_to: globalParams.get('date_created_to') || ''
            },
            vet: localStorage.getItem('vet') ? JSON.parse(localStorage.getItem('vet')) : {}
        };
        this.getBookingList(this.state.currentFilter);
        this.getServiceTypeList();
        this.headers = Object.freeze([
            {
                name: 'Booking ID',
                id: 'orderId',
                className: 'order-id',
            },
            {
                name: 'Pet Tag',
                id: 'img',
                className: 'pet-tag',
                disableSort: true,
            },
            {
                name: 'Service Type',
                id: 'type',
            },
            {
                name: 'Date Created',
                id: 'dateEntered',
                className: 'date-created',
            },
            {
                name: 'Owner Name',
                id: 'ownerName',
            },
            {
                name: 'Pet Name',
                id: 'petName',
            },
            {
                name: 'Status',
                id: 'orderStage',
            },
            {
                name: 'Action',
                id: 'action',
                disableSort: true
            },
        ]);
        localStorage.removeItem('service-type-name')
        this.debouncedHandleSearchOnChange = this.debouncedHandleSearchOnChange.bind(this);
    }

    /**
     * Get the list of bookings
     * @param {Object} additionalFilters Additional filters to apply to the booking list
     * @returns {void}
     */
    getBookingList(additionalFilters = undefined) {
        const bookingData = {
            page: this.state.currentPage,
            page_size: 15,
            ...additionalFilters,
        };

        // Helper to map bookings with the current statusDisplayMap
        const mapBookings = (bookings, statusDisplayMap) => {
            return bookings.map((booking) => {
                const bookingObj = newBooking(booking);
                let withCremationPackage = false;
                const productsWithCremationPackage = bookingObj.quotedProducts.find((product) => {
                    if (product.name.toLowerCase().includes('cremation package')) {
                        return product;
                    }
                });
                if (productsWithCremationPackage) {
                    withCremationPackage = true;
                }

                // get path
                let editBookingPath = EDIT_BOOKING_PATH.find((editBookingPath) => {
                    // if package
                    if (withCremationPackage || bookingObj.isPackage) {
                        if (editBookingPath.path === "package") {
                            return editBookingPath;
                        }
                    } else if (bookingObj.opportunityType === "referral" &&
                        (bookingObj.serviceType.name === "Private Cremation & Return of Ashes" ||
                            bookingObj.serviceType.name === "Private Cremation & Dispersal of Ashes"
                        )
                    ) { // if private cremation & return of ashes referral
                        if (editBookingPath.path === "referral") {
                            return editBookingPath;
                        }
                    } else if (editBookingPath.serviceType === bookingObj.serviceType.name) {
                        return editBookingPath.path;
                    }
                });

                // for vet unity
                if (editBookingPath?.path && bookingObj?.opportunityType === 'vet_unity') {
                    if (!editBookingPath.path.includes('vetunity') && (!editBookingPath.path.includes('memorabilia'))) {
                        editBookingPath.path = `vetunity-${editBookingPath.path}`;
                    }
                }

                let bookingImage = bookingObj.getImage();

                // Map the orderStage using the statusDisplayMap if available
                let mappedOrderStage = bookingObj.orderStage;
                if (statusDisplayMap && statusDisplayMap[bookingObj.orderStage]) {
                    mappedOrderStage = statusDisplayMap[bookingObj.orderStage];
                }

                return {
                    img: <BookingImage bookingImage={bookingImage} onClick={(evt, imageUrl) => {
                        // Only change the lightbox state for non-fallback images
                        if (evt.target.src !== window.location.origin + imageFallback) {
                            this.setState({
                                objLightBox: {
                                    index: bookingObj.orderId,
                                    url: imageUrl,
                                    onClose: (e) => {
                                        this.setState({ objLightBox: {} }, () => document.body.style.overflow = 'unset');
                                    }
                                },
                            }, () => document.body.style.overflow = 'hidden');
                        }
                    }} />,
                    ...bookingObj.customTableFormat(),
                    orderStage: mappedOrderStage,
                    action: (
                        <>
                            {bookingObj.isEditable() && editBookingPath?.path && mappedOrderStage === "Unsubmitted" ?
                                <Link
                                    to={`/edit-booking-${editBookingPath.path}/${booking.id}`}
                                    onClick={() => localStorage.setItem('service-type-name', bookingObj?.serviceType?.name)}
                                >Edit</Link> : null
                            }
                            <Link to={`/booking/${booking.id}`}>View</Link>
                        </>
                    )
                };
            });
        };

        // If mapping is not loaded, fetch it first, then fetch bookings and apply mapping
        if (Object.keys(this.state.statusDisplayMap).length === 0) {
            getOrderStages().then((response) => {
                this.setState({ statusDisplayMap: response }, () => {
                    getBookings(bookingData)
                        .then((res) => {
                            this.setState({ lastPage: res.data.last_page });
                            const bookings = mapBookings.call(this, res.data.data, response);
                            this.setState({ allBookings: bookings, bookingList: bookings });
                        })
                        .catch((err) => {
                            console.error('err :>> ', err);
                        });
                });
            });
        } else {
            // Mapping is already loaded, fetch bookings and apply mapping immediately
            getBookings(bookingData)
                .then((res) => {
                    this.setState({ lastPage: res.data.last_page });
                    const bookings = mapBookings.call(this, res.data.data, this.state.statusDisplayMap);
                    this.setState({ allBookings: bookings, bookingList: bookings });
                })
                .catch((err) => {
                    console.error('err :>> ', err);
                });
        }
    }

    /**
     * Get the list of service types for the vet
     * 
     * This is used to populate the service type filter in the bookings page.
     * @returns {void}
     */
    getServiceTypeList() {
        getVetServices()
            .then((res) => {
                this.setState({ serviceTypeList: res.data });
            })
            .catch((err) => {
                console.error('err :>> ', err);
            });
    }

    /**
     * Get the keys for a specific order stage
     * @param {string} orderStage The order stage to get keys for
     * @returns {string[]} Array of keys corresponding to the order stage
     */
    getOrderStageKeys(orderStage) {
        return Object.keys(this.state.statusDisplayMap).filter(key => this.state.statusDisplayMap[key] === orderStage);
    }

    /**
     * Handle search filter changes
     * @param {Object} value The search filter values
     * @param {string} value.searchTerm The search term
     * @param {string} value.serviceType The service type
     * @param {string} value.serviceStatus The service status
     * @param {Object} value.dateCreated The date created range
     * @param {string} value.dateCreated.from The start date
     * @param {string} value.dateCreated.to The end date
     * @returns {void}
     */
    handleSearchOnChange = (value) => {
        const additionalFilters = {
            search_term: value.searchTerm,
            service_type: value.serviceType,
            service_status: this.getOrderStageKeys(value.serviceStatus),
            service_status_label: value.serviceStatusLabel || '',
            date_created_from: value.dateCreated.from,
            date_created_to: value.dateCreated.to,
        };
        const filterDidChange = JSON.stringify(this.state.currentFilter) !== JSON.stringify(additionalFilters);
        if (filterDidChange) {
            this.setState({ currentFilter: additionalFilters });
            this.setState({ currentPage: 1 });
            this.getBookingList(this.state.currentFilter);
        }
    };

    /**
     * Debounced version of handleSearchOnChange to limit the number of API calls
     * @param {Object} value The search filter values
     * @param {string} value.searchTerm The search term 
     * @param {string} value.serviceType The service type
     * @param {string} value.serviceStatus The service status
     * @param {Object} value.dateCreated The date created range
     * @param {string} value.dateCreated.from The start date
     * @param {string} value.dateCreated.to The end date
     * @returns {void}
     */
    debouncedHandleSearchOnChange(value) {
        if (this.debounceTimeout !== undefined) {
        clearTimeout(this.debounceTimeout);
        }
        this.debounceTimeout = window.setTimeout(() => {
        this.handleSearchOnChange(value);
        }, 1000);
    }

    isEmptyFilter() {
        return Object.values(this.state.currentFilter).every(value => !value || value === '' || (Array.isArray(value) && value.length === 0));
    }

    componentWillUnmount() {
        if (this.debounceTimeout !== undefined) {
            clearTimeout(this.debounceTimeout);
        }
    }

    render() {
        return (
            <div className="main-container">
                <NavbarSecondary />
                <NavbarPrimary />
                <div className="body-content">
                    <PageTitle title=" View Bookings/Orders" />
                    <ContentCard
                        className="mt-62"
                        divider={false}
                        headerBtnText="View more"
                        headerStyle={{ paddingBottom: 0 }}
                        headerSubComponent={
                            <div className="m-4" />
                        }
                    >
                        <ContainerHeader
                            title="Latest entries"
                        />
                        <Filter data={this.state.allBookings} serviceTypeList={this.state.serviceTypeList} objStatusMapRaw={this.state.statusDisplayMap} statusMap={Array.from(new Set(Object.values(this.state.statusDisplayMap)))} handleOnChangeSearchFilter={this.debouncedHandleSearchOnChange} globalFilterDefaultValues={this.state.currentFilter}/>
                        <CustomTable
                            data={this.state.bookingList}
                            headers={this.headers}
                            className="dashboard-table"
                            disableSort={ false }
                            lightBox={this.state.objLightBox}
                            defaultSortBy={
                                {
                                    id: 'dateEntered',
                                    desc: true,
                                }
                            }
                        />
                        <div style={{width: '100%', textAlign: 'center', marginTop: '20px', color: '#999'}}>
                        {
                            this.state.bookingList.length < 1 ? (this.isEmptyFilter() ? 'No Bookings Yet' : 'No Bookings Found') : null
                        }
                        </div>
                        <Pagination className="booking-paginator" currentPage={this.state.currentPage} lastPage={this.state.lastPage} additionalFilters={this.state.currentFilter} />
                    </ContentCard>
                </div>
            </div>
        );
    }
}

export default withRouter(Bookings);
